'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { sendGTMEvent } from '@/utils/analytics';

import { useGlobal } from '@/contexts/GlobalContext';
import { useDrawer } from '@/contexts/DrawerContext';
import { useTacticalboardDrawer } from '@/contexts/TacticalboardDrawerContext';
import { useNotificationContext } from '@/contexts/NotificationContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import ExpiredTicketModalContent from '@/components/tickets/ExpiredTicketModalContent';
import InformationModalContent from '@/components/layouts/InformationModalContent';
import Button from '@/components/common/Button';
import FootballlogDrawer from '@/components/footballlogs/FootballlogDrawer';
import TacticalboardDrawer from '@/components/tacticalboard/TacticalboardDrawer';
import { formatYYYYmmWithHyphen } from '@/utils/date';
import LoginCardMenu from '@/components/layouts/LoginCardMenu';
import { Menu } from '@/types/menu';
import { getCurrentMenu } from '@/utils/getCurrentMenu';

export default function SideMenu() {
  const location = useLocation();
  const pathname = location.pathname;

  /**
   * React Context
   */
  const context = useGlobal();
  const { openDrawer, closeDrawer } = useTacticalboardDrawer();
  const { openDrawer: openNewNoteDrawer } = useDrawer();
  const { allConfirmed } = useNotificationContext();
  const { openConfirmModal } = useConfirmModal();

  /**
   * useState
   */
  const [currentMenu, setCurrentMenu] = useState<Menu | undefined>(undefined);

  // サッカーノートカレンダーのデフォルト日付を設定
  let date: Date;
  if (context.systemDate != null) {
    date = new Date(context.systemDate);
  } else {
    date = new Date();
  }
  const systemDateYearMonth = formatYYYYmmWithHyphen(date);

  /**
   * ドロワー表示（サッカーボード）
   */
  const handleOpenDrawer = () => {
    sendGTMEvent('openTacticalboardv2');
    openDrawer(<TacticalboardDrawer onCloseDrawer={closeDrawer} />);
  };

  /**
   * ドロワー表示（サッカーノート新規）
   */
  const handleOpenNewNoteDrawer = () => {
    // FeatureFlag制御: 機能OFF時は有効期限チェックしない
    if (context.isFeatureEnabled('ticket_payment')) {
      // チームプランのみ適用中の場合
      if (context.user?.has_team_plan_only) {
        openConfirmModal(
          <InformationModalContent>
            チームプラン適用中です。
            <br />
            チームページにて、ノート作成が可能です。
          </InformationModalContent>
        );
        return;
      }

      // チケット有効期限切れの場合
      if (!context.user?.has_ticket) {
        openConfirmModal(<ExpiredTicketModalContent />);
        return;
      }
    }

    openNewNoteDrawer(<FootballlogDrawer />);
  };

  useEffect(() => {
    const menu = getCurrentMenu(pathname);
    setCurrentMenu(menu);
  }, [pathname]);

  return (
    <div className="select-none flex flex-col justify-between h-full overflow-x-hidden">
      <ul className="px-4">
        <li className="my-1">
          <Link
            to="/home"
            className={`flex items-center justify-start text-xl text-white ${currentMenu === 'HOME' && 'font-semibold bg-green-500 bg-opacity-20 rounded-full'} px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full`}>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
              />
            </svg>
            <div className="ms-6">HOME</div>
          </Link>
        </li>
        <li className="my-1">
          <Link
            to={`/notes/calendar/${systemDateYearMonth}`}
            className={`flex items-center justify-start text-xl text-white ${currentMenu === 'NOTE' && 'font-semibold bg-green-500 bg-opacity-20 rounded-full'} px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full`}>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6.03v13m0-13c-2.819-.831-4.715-1.076-8.029-1.023A.99.99 0 0 0 3 6v11c0 .563.466 1.014 1.03 1.007 3.122-.043 5.018.212 7.97 1.023m0-13c2.819-.831 4.715-1.076 8.029-1.023A.99.99 0 0 1 21 6v11c0 .563-.466 1.014-1.03 1.007-3.122-.043-5.018.212-7.97 1.023"
              />
            </svg>
            <div className="ms-6">サッカーノート</div>
          </Link>
        </li>
        <li className="my-1">
          <Link
            to="/goals"
            className={`flex items-center justify-start text-xl text-white ${currentMenu === 'GOAL' && 'font-semibold bg-green-500 bg-opacity-20 rounded-full'} px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full`}>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-6 7 2 2 4-4m-5-9v4h4V3h-4Z"
              />
            </svg>
            <div className="ms-6">目標・課題</div>
          </Link>
        </li>
        <li className="my-1">
          <Link
            to="/supporters"
            className={`flex items-center justify-start text-xl text-white ${currentMenu === 'SUPPORTER' && 'font-semibold bg-green-500 bg-opacity-20 rounded-full'} px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full`}>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                d="M4.5 17H4a1 1 0 0 1-1-1 3 3 0 0 1 3-3h1m0-3.05A2.5 2.5 0 1 1 9 5.5M19.5 17h.5a1 1 0 0 0 1-1 3 3 0 0 0-3-3h-1m0-3.05a2.5 2.5 0 1 0-2-4.45m.5 13.5h-7a1 1 0 0 1-1-1 3 3 0 0 1 3-3h3a3 3 0 0 1 3 3 1 1 0 0 1-1 1Zm-1-9.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"
              />
            </svg>
            <div className="ms-6">サポーター</div>
          </Link>
        </li>
        <li className="my-1">
          <div
            onClick={handleOpenDrawer}
            className="flex items-center justify-start text-xl text-white px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full">
            {/* <svg className="w-6 h-6" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                stroke="currentColor"
                d="M1 10H17M2 19H16C16.5523 19 17 18.5523 17 18V2C17 1.44772 16.5523 1 16 1H2C1.44772 1 1 1.44772 1 2V18C1 18.5523 1.44772 19 2 19ZM7 4H11C11.5523 4 12 3.55228 12 3V2C12 1.44772 11.5523 1 11 1H7C6.44772 1 6 1.44772 6 2V3C6 3.55228 6.44772 4 7 4ZM7 19H11C11.5523 19 12 18.5523 12 18V17C12 16.4477 11.5523 16 11 16H7C6.44772 16 6 16.4477 6 17V18C6 18.5523 6.44772 19 7 19ZM12 10C12 11.6569 10.6569 13 9 13C7.34315 13 6 11.6569 6 10C6 8.34315 7.34315 7 9 7C10.6569 7 12 8.34315 12 10Z"
                strokeWidth="2"
              />
            </svg> */}
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 12H20M5 21H19C19.5523 21 20 20.5523 20 20V4C20 3.44772 19.5523 3 19 3H5C4.44772 3 4 3.44772 4 4V20C4 20.5523 4.44772 21 5 21ZM10 6H14C14.5523 6 15 5.55228 15 5V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V5C9 5.55228 9.44772 6 10 6ZM10 21H14C14.5523 21 15 20.5523 15 20V19C15 18.4477 14.5523 18 14 18H10C9.44772 18 9 18.4477 9 19V20C9 20.5523 9.44772 21 10 21ZM15 12C15 13.6569 13.6569 15 12 15C10.3432 15 9 13.6569 9 12C9 10.3432 10.3432 9 12 9C13.6569 9 15 10.3432 15 12Z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <div className="ms-6">サッカーボード</div>
          </div>
        </li>
        <li className="my-1">
          <Link
            to="/videos"
            className={`flex items-center justify-start text-xl text-white ${currentMenu === 'VIDEO' && 'font-semibold bg-green-500 bg-opacity-20 rounded-full'} px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full`}>
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M21.7 8.03694C21.6064 7.32632 21.335 6.6508 20.911 6.07294C20.3855 5.54358 19.6728 5.24221 18.927 5.23394C16.16 5.03394 12.001 5.03394 12.001 5.03394C12.001 5.03394 7.84398 5.03394 5.07298 5.23394C4.32738 5.24204 3.61498 5.54346 3.08998 6.07294C2.66409 6.65024 2.39221 7.32649 2.29998 8.03794C2.17638 9.10229 2.10962 10.1725 2.09998 11.2439V12.7439C2.10957 13.8154 2.17633 14.8856 2.29998 15.9499C2.39398 16.6619 2.66398 17.3399 3.08398 17.9219C3.68798 18.4579 4.46398 18.7589 5.27098 18.7699C6.85398 18.9209 12.002 18.9699 12.002 18.9699C12.002 18.9699 16.163 18.9699 18.93 18.7699C19.6762 18.7612 20.3892 18.4595 20.915 17.9299C21.3381 17.3514 21.6087 16.6756 21.702 15.9649C21.8256 14.9006 21.8924 13.8304 21.902 12.7589V11.2429C21.8907 10.1715 21.8233 9.10134 21.7 8.03694Z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M15 12L9.75 14.5981V9.40192L15 12Z" fill="currentColor" />
            </svg>
            <div className="ms-6">サッカー動画</div>
          </Link>
        </li>
        <li className="my-1">
          <Link
            to="/notifications"
            className={`flex items-center justify-start text-xl text-white ${currentMenu === 'NOTIFICATION' && 'font-semibold bg-green-500 bg-opacity-20 rounded-full'} px-4 py-3 rounded-full transition duration-300 hover:bg-green-500 hover:bg-opacity-30 hover:rounded-full`}>
            <div className="relative">
              <svg
                className="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 5.365V3m0 2.365a5.338 5.338 0 0 1 5.133 5.368v1.8c0 2.386 1.867 2.982 1.867 4.175 0 .593 0 1.292-.538 1.292H5.538C5 18 5 17.301 5 16.708c0-1.193 1.867-1.789 1.867-4.175v-1.8A5.338 5.338 0 0 1 12 5.365ZM8.733 18c.094.852.306 1.54.944 2.112a3.48 3.48 0 0 0 4.646 0c.638-.572 1.236-1.26 1.33-2.112h-6.92Z"
                />
              </svg>
              {!allConfirmed && (
                <div className="absolute top-0 right-0 w-3 h-3 rounded-full border border-white bg-blue-600"></div>
              )}
            </div>
            <div className="ms-6">通知</div>
          </Link>
        </li>
        <li className="p-4">
          <Button type="button" className="w-full p-4 h-12" onClick={handleOpenNewNoteDrawer}>
            <svg
              className="w-4 me-2 text-white dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 18 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 1v16M1 9h16"
              />
            </svg>
            <span className="text-lg">新しいノート</span>
          </Button>
        </li>
      </ul>
      {/* <div>
      userId: {context.user?.id}<br/>
        account_name: {context.user?.account_name}<br/>
        email: {context.user?.masked_email}<br/>
        date: {context.systemDate}<br/>
        csrfToken: {context.csrftoken}<br/>
      </div> */}
      <div className="sticky bottom-0 p-1">
        <LoginCardMenu />
      </div>
    </div>
  );
}
